import { ArrowLeft, Check, FilmStrip } from '@phosphor-icons/react';
import type { MediaAssetDto } from '@reelplicate/api-client/model';
import {
  Button,
  EmptyState,
  Inline,
  Modal,
  ModalClose,
  ModalContent,
  SearchField,
  Skeleton,
  Stack,
} from '@reelplicate/ui';
import { useState } from 'react';

import { formatDuration } from '@/features/media/library';
import { timecode } from '@/features/media/scenes';

import { useReelsWithScenes } from './reels-with-scenes';

export type ReelScenesPick = { mediaAssetId: string; scenes: number[] };

const matches = (reel: MediaAssetDto, query: string) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return [
    reel.title,
    reel.savedFrom,
    reel.caption,
    reel.transcript,
    ...(reel.scenes ?? []).flatMap((s) => [s.text, s.context]),
  ].some((text) => text?.toLowerCase().includes(q));
};

/**
 * Reel scenes' picker: a reel from the library, then which of its scenes to
 * remake - none ticked means all of them.
 */
export function ReelScenesModal({
  open,
  onOpenChange,
  workspaceId,
  value,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  value: Partial<ReelScenesPick>;
  onPick: (pick: ReelScenesPick) => void;
}) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {open && (
        <ReelScenes
          workspaceId={workspaceId}
          value={value}
          onPick={(pick) => {
            onPick(pick);
            onOpenChange(false);
          }}
        />
      )}
    </Modal>
  );
}

function ReelScenes({
  workspaceId,
  value,
  onPick,
}: {
  workspaceId: string;
  value: Partial<ReelScenesPick>;
  onPick: (pick: ReelScenesPick) => void;
}) {
  const { reels, loading } = useReelsWithScenes(workspaceId);
  const [reelId, setReelId] = useState(value.mediaAssetId);
  const [picked, setPicked] = useState<number[]>(value.scenes ?? []);
  const [query, setQuery] = useState('');
  const reel = reels.find((r) => r.id === reelId);
  const scenes = reel?.scenes ?? [];

  const open = (id: string) => {
    // Another reel's scene numbers mean nothing here.
    if (id !== reelId) setPicked(id === value.mediaAssetId ? (value.scenes ?? []) : []);
    setReelId(id);
  };
  const toggle = (n: number) =>
    setPicked((prev) =>
      prev.includes(n) ? prev.filter((p) => p !== n) : [...prev, n].sort((a, b) => a - b),
    );

  const summary = !reel
    ? 'Pick a reel, then its scenes.'
    : picked.length === 0
      ? `All ${scenes.length} scenes - none ticked`
      : `${picked.length} of ${scenes.length} scenes`;

  return (
    <ModalContent
      title={reel ? reel.title : 'Choose a reel'}
      description={
        reel
          ? 'Tick the scenes to remake. None ticked takes them all.'
          : 'Reels in your library with a scene breakdown.'
      }
      className="h-[min(760px,calc(100vh-64px))] w-230 [&_[data-slot=modal-footer]]:bg-bg-surface"
      footerStart={
        <span className="text-body-sm text-text-secondary truncate">{summary}</span>
      }
      footer={
        <>
          <ModalClose asChild>
            <Button variant="outline">Cancel</Button>
          </ModalClose>
          <Button
            disabled={!reel}
            onClick={() => reel && onPick({ mediaAssetId: reel.id, scenes: picked })}
          >
            Use these scenes
          </Button>
        </>
      }
    >
      {reel ? (
        <Stack gap={4}>
          <Inline
            gap={2}
            alignY="center"
            className="bg-bg-surface sticky top-0 z-10 pb-2"
          >
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={ArrowLeft}
              onClick={() => setReelId(undefined)}
            >
              All reels
            </Button>
            <span className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPicked(scenes.map((s) => s.scene))}
            >
              Select all
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={picked.length === 0}
              onClick={() => setPicked([])}
            >
              Clear
            </Button>
          </Inline>
          <ul aria-label="Scenes" className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {scenes.map((scene) => {
              const on = picked.includes(scene.scene);
              return (
                <li key={scene.scene}>
                  <button
                    type="button"
                    aria-pressed={on}
                    aria-label={`Scene ${scene.scene}`}
                    onClick={() => toggle(scene.scene)}
                    className="focus-visible:shadow-focus-ring group flex w-full flex-col gap-2 rounded-lg text-left outline-none"
                  >
                    <span
                      className={`relative block aspect-[9/16] w-full overflow-hidden rounded-lg ${
                        on
                          ? 'ring-border-brand ring-2 ring-offset-2'
                          : 'ring-border-subtle ring-1'
                      }`}
                    >
                      {scene.frameUrl ? (
                        <img
                          src={scene.frameUrl}
                          alt=""
                          className="size-full object-cover"
                        />
                      ) : (
                        <span className="bg-bg-muted block size-full" />
                      )}
                      <span
                        className={`absolute top-2 right-2 grid size-6 place-items-center rounded-full border ${
                          on
                            ? 'bg-brand-primary text-text-on-brand border-transparent'
                            : 'border-white/80 bg-black/20 text-transparent'
                        }`}
                      >
                        <Check size={14} weight="bold" />
                      </span>
                    </span>
                    <span className="text-caption text-text-tertiary tabular-nums">
                      Scene {scene.scene} · {timecode(scene.start)}–{timecode(scene.end)}
                    </span>
                    <span className="text-body-sm text-text-primary line-clamp-2">
                      {scene.text ?? scene.context ?? ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Stack>
      ) : (
        <Stack gap={4}>
          <div className="bg-bg-surface sticky top-0 z-10 pb-2">
            <SearchField
              aria-label="Search reels"
              placeholder="Search by title, @handle, caption or what’s on screen"
              value={query}
              onValueChange={setQuery}
              shortcut={false}
            />
          </div>
          {loading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} shape="rectangle" shimmer className="aspect-[9/16]" />
              ))}
            </div>
          ) : reels.length === 0 ? (
            <EmptyState
              icon={FilmStrip}
              title="No reels with scenes yet"
              description="Save a reel from a link or a competitor in Media - its scenes are read when it’s saved."
            />
          ) : (
            <ReelGrid
              reels={reels.filter((r) => matches(r, query.trim()))}
              current={value.mediaAssetId}
              onOpen={open}
            />
          )}
        </Stack>
      )}
    </ModalContent>
  );
}

function ReelGrid({
  reels,
  current,
  onOpen,
}: {
  reels: MediaAssetDto[];
  current?: string;
  onOpen: (id: string) => void;
}) {
  if (reels.length === 0) {
    return (
      <EmptyState icon={FilmStrip} title="No matches" description="Try another search." />
    );
  }
  return (
    <ul aria-label="Reels" className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      {reels.map((reel) => (
        <li key={reel.id}>
          <button
            type="button"
            aria-label={reel.title}
            aria-current={reel.id === current || undefined}
            onClick={() => onOpen(reel.id)}
            className="focus-visible:shadow-focus-ring flex w-full flex-col gap-2 rounded-lg text-left outline-none"
          >
            <span
              className={`relative block aspect-[9/16] w-full overflow-hidden rounded-lg ${
                reel.id === current
                  ? 'ring-border-brand ring-2 ring-offset-2'
                  : 'ring-border-subtle ring-1'
              }`}
            >
              {reel.thumbnailUrl ? (
                <img src={reel.thumbnailUrl} alt="" className="size-full object-cover" />
              ) : (
                <span className="bg-bg-muted block size-full" />
              )}
              <span className="text-caption absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-white tabular-nums">
                {reel.scenes!.length} scenes
                {reel.durationSeconds ? ` · ${formatDuration(reel.durationSeconds)}` : ''}
              </span>
            </span>
            <span className="text-body-sm text-text-primary line-clamp-2">
              {reel.title}
            </span>
            {reel.savedFrom && (
              <span className="text-caption text-text-tertiary truncate">
                {reel.savedFrom}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
